import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import ModelTypeConstants, {STORABLE_ITEM_TYPES, StorableItemType} from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import AllStorablesSchemaSelector, {AllStorablesSelectorProps} from 'samples/AllStorablesSchemaSelector';
import {isSchemaTypeFilterOption} from 'schemas/SchemaConstants';
import {isSchemaInterface} from 'schemas/SchemaPredicates';
import QueryingTagSchemaOrInterfaceSelector, {
  TagSchemaOrInterfaceSelectorProps,
} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import ImmutableSet from 'util/ImmutableSet';
import {capitalize, typeToName} from 'util/NameUtil';

import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import {NonArrayDefinition} from './DefineTemplateVariablesEditor';

export type DomainDefinition = Extract<NonArrayDefinition, {kind: 'domain'}>;

export function isDomainDefinition(
  config: NonArrayDefinition | null | undefined
): config is DomainDefinition {
  return config != null && config.kind === 'domain' && Array.isArray(config.types);
}

const SUPPORTED_FOLDER_ITEM_MODEL_TYPES: ImmutableSet<OligoAwareFolderItemType> = new ImmutableSet([
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.RNA_SEQUENCE,
]);

interface DomainTypeSchemaSelectorProps {
  config: DomainDefinition;
  onChangeConfig: (config: NonArrayDefinition) => void;
  disabled?: boolean;
}

/**
 * Component to allow setting schema or schema interface filters for domain type variables.
 *
 * When a domain type variable is defined, users can optionally restrict it to:
 * - A specific schema (schemaIds)
 * - A schema interface (schemaInterfaceIds) - only for TagSchema-backed types.
 *
 * For TagSchema-backed types:
 *   Uses QueryingTagSchemaOrInterfaceSelector which supports both schemas and interfaces.
 *
 * For StorableSchema-backed types:
 *   Uses AllStorablesSchemaSelector. Only plates are supported.
 *
 * For types without schemas:
 *   Returns null (no selector shown).
 */
export default function DomainTypeSchemaSelector({
  config,
  onChangeConfig,
  disabled,
}: DomainTypeSchemaSelectorProps): JSX.Element | null {
  // A schema cannot be selected until a domain type is selected
  if (config.types == null || config.types.length === 0) {
    return null;
  }

  // `config.schemaIds` and `config.schemaInterfaceIds` are arrays with `maxItems=1`
  const selectedSchemaOrInterfaceId =
    (config.schemaIds ?? [])[0] ?? (config.schemaInterfaceIds ?? [])[0] ?? null;
  // config.types is an array with maxItems=1, so we just need the first element
  const selectedDomainType = config.types[0]!;
  // The schema selector components expect a `ModelTypeConstants`, rather than a domain type.
  const legacyModelType = getModelTypeConstantForObjectType(selectedDomainType);

  const handleSchemaChange = (
    selected:
      | Parameters<AllStorablesSelectorProps['onChange']>[0]
      | Parameters<TagSchemaOrInterfaceSelectorProps['onChange']>[0]
  ): void => {
    if (selected == null || isSchemaTypeFilterOption(selected)) {
      onChangeConfig({...config, schemaIds: null, schemaInterfaceIds: null});
    } else if (isSchemaInterface(selected)) {
      onChangeConfig({...config, schemaIds: null, schemaInterfaceIds: [selected.id]});
    } else {
      onChangeConfig({...config, schemaIds: [selected.id], schemaInterfaceIds: null});
    }
  };

  const handleClear = (): void => {
    handleSchemaChange(null);
  };

  // This model type does not yet support schema selection
  if (
    !SUPPORTED_FOLDER_ITEM_MODEL_TYPES.has(legacyModelType as OligoAwareFolderItemType) &&
    !STORABLE_ITEM_TYPES.includes(legacyModelType)
  ) {
    return null;
  }

  const typeDisplayName = capitalize(typeToName(legacyModelType));

  return (
    <div className={css(styles.container)}>
      <VariableHeaderOrNull variableName={`${typeDisplayName} schema`} isCompact={false} />
      <div className={css(styles.selectorRow)}>
        <div className={css(styles.selectorWrapper)}>
          {STORABLE_ITEM_TYPES.includes(legacyModelType) ? (
            <AllStorablesSchemaSelector
              includeStorableTypes={[legacyModelType as StorableItemType]}
              selectedAllStorablesSchemaId={selectedSchemaOrInterfaceId}
              onChange={handleSchemaChange}
              shouldShowNoSchemaOption={true}
              isDisabled={disabled}
            />
          ) : (
            <QueryingTagSchemaOrInterfaceSelector
              allowInterfaces={true}
              allowNone={true}
              oligoAwareFolderItemTypes={[legacyModelType as OligoAwareFolderItemType]}
              selectedTagSchemaOrInterfaceId={selectedSchemaOrInterfaceId}
              onChange={handleSchemaChange}
              isDisabled={disabled}
            />
          )}
        </div>
        {selectedSchemaOrInterfaceId && !disabled && (
          <div className={css(styles.clearIconWrapper)}>
            <DeleteIcon icon="cross-thick" onClick={handleClear} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    marginBottom: 8,
  },
  selectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  selectorWrapper: {
    flex: 1,
  },
  clearIconWrapper: {
    cursor: 'pointer',
  },
});
